/**
 * The merged public surface: what is large, what is small, and what a control
 * does. Wave 5, call 2.
 *
 * `viewer.html` and `publish.html` were two pages that could not both be open on
 * one phone, so a publisher filmed blind: no way to see whether they were on
 * air or what the audience was seeing. They are now one page laid out like a
 * video call, and everything here is the part of that with a decision in it.
 *
 * Pure, because `src/client/` is the half of this repository with no runtime
 * harness (issue 010), and because a rule living in a click handler is a rule
 * nothing can test. Every claim call 2 makes is expressible without a DOM, and
 * that is deliberate rather than convenient.
 */

/** Where a person is in the one flow this page has. */
export type PublishState =
  /** Not publishing. An audience member. */
  | 'watching'
  /** Publishing, and not on air: something is being fixed. */
  | 'offered'
  /** Publishing and on air. */
  | 'onair'
  /** Was on air and is not any more, which is the moment most owed an answer. */
  | 'takenoff';

/** Which of the two pictures gets the screen. */
export type MainView = 'broadcast' | 'camera';

export function publishState(
  publishing: boolean,
  live: boolean,
  wasOnAir: boolean,
): PublishState {
  if (!publishing) return 'watching';
  if (live) return 'onair';
  return wasOnAir ? 'takenoff' : 'offered';
}

/**
 * **The main view shows whatever the person can act on.**
 *
 * Off air, the thing they can act on is their own framing, and the sheet is
 * telling them the picture is too dark while the viewfinder is the instrument
 * that fixes it. On air there is nothing left to fix, so the payoff takes the
 * screen, and that is also where the 32s gap teaches itself: you are large and
 * 32 seconds ago, and small and now.
 *
 * `pinned` is the guard. A view that reframes itself under somebody's thumb is
 * infuriating, and the second time it does it against their wishes they stop
 * trusting the page. So the rule follows you until you disagree with it once,
 * and then never again. Same contract as a log tail that follows the bottom
 * until you scroll up.
 */
export function mainView(state: PublishState, pinned: MainView | null): MainView {
  if (pinned !== null) return pinned;
  switch (state) {
    case 'watching': return 'broadcast';
    case 'offered': return 'camera';
    case 'onair': return 'broadcast';
    case 'takenoff': return 'camera';
  }
}

/**
 * While publishing, the broadcast is muted, and nothing can unmute it.
 *
 * An open mic beside a speaker playing the broadcast puts the delayed broadcast
 * audio back into the feed, which goes out and returns 32s later: a slow
 * feedback loop that also poisons the speech gate, because the transcriber
 * would be reading the broadcast rather than the gym. Headphones would make it
 * safe and cannot be reliably detected, so the rule is unconditional.
 *
 * Returned as a decision rather than enforced at the element, so the sound
 * control can be DISABLED with a reason rather than silently doing nothing.
 */
export function broadcastMuted(state: PublishState, wantsSound: boolean): boolean {
  if (state !== 'watching') return true;
  return !wantsSound;
}

/** Why the sound control is unavailable, or null when it is available. */
export function soundBlockedReason(state: PublishState): string | null {
  return state === 'watching'
    ? null
    : 'Muted while you are streaming, so the sound does not loop back into your microphone.';
}

/**
 * Confirmation is inverted from where it usually goes.
 *
 * No confirm to start: the gates are the preview, and being refused is the
 * normal, documented state this product is built to explain, so a
 * frame-then-confirm step would duplicate, worse, the thing the system already
 * does well. A confirm to STOP WHILE ON AIR, because that is the tap with a
 * consequence: you are the broadcast, and stopping may cut it to the slate.
 *
 * All the friction was at the front, where it costs a publisher, and none at
 * the back, where it costs the audience.
 */
export function stopNeedsConfirm(state: PublishState): boolean {
  return state === 'onair';
}

/** What the primary control says. Exactly one high-contrast control per state. */
export function primaryAction(state: PublishState): { label: string; kind: 'start' | 'stop' } {
  return state === 'watching'
    ? { label: 'Go live', kind: 'start' }
    : { label: 'Stop streaming', kind: 'stop' };
}

/**
 * The one line under the small tile, which is the whole reason the two pictures
 * are on one screen.
 *
 * Only says the hold when it is actually demonstrating it. On air, the two
 * tiles are the same camera at two times and saying so is the cheapest
 * explanation of the delay in the product. Off air they are different things
 * and claiming a relationship between them would be a new dishonesty.
 */
export function tileRelation(state: PublishState, holdSeconds: number | null): string {
  if (state === 'onair' && holdSeconds !== null) {
    return `You, right now. The big picture is you ${holdSeconds}s ago, after the checks.`;
  }
  if (state === 'onair') return 'You, right now.';
  if (state === 'watching') return '';
  return 'Your camera. Not live yet.';
}

/**
 * The one line a publisher gets without opening anything.
 *
 * The ruling, the fate and the coaching are three separate artifacts behind the
 * panel, and they stay there: wave 3 built them separable on purpose and
 * merging them is what `separable()` refuses. This is not a fourth artifact, it
 * is the FIRST BLOCKER surfaced where the eye already is.
 *
 * Reported 2026-08-29: a publisher could not find any of it. The panel was one
 * tap away and the tap was the problem, because the reason you are not live is
 * the thing you are trying to fix, and this page's own rule is that the main
 * view shows whatever the person can act on. A reason behind a tap is not.
 *
 * Deliberately the RULING and never the coaching. A suggestion promoted to the
 * most prominent line on the page teaches that advice is binding, which is the
 * lesson proposal 7.2.1 exists to prevent.
 */
export function publisherHeadline(
  state: PublishState,
  blockers: readonly string[],
  panel: string,
): string {
  if (state === 'watching') return '';
  if (state === 'onair') return 'You are live.';
  if (blockers.length === 0) return `Ready. Tap "${panel}" to name yourself and your seat.`;
  const first = blockers[0] ?? '';
  const more = blockers.length > 1 ? ` (+${blockers.length - 1} more)` : '';
  const lead = state === 'takenoff' ? 'Taken off' : 'Not live yet';
  return `${lead}: ${first}${more}. Tap "${panel}" for the full ruling.`;
}
